import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import Map, { Marker, NavigationControl, ViewStateChangeEvent, Source, Layer } from 'react-map-gl';
import { Box, Paper, Typography, Tooltip, TextField, InputAdornment, IconButton, Autocomplete, CircularProgress, Snackbar, Alert } from '@mui/material';
import 'mapbox-gl/dist/mapbox-gl.css';
import VineyardRowsOverlay from './VineyardRowsOverlay';
import { SavedLocation } from '../types';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import * as GeoJSON from 'geojson';

// Get the Mapbox token from environment
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

// Validate token
if (!MAPBOX_TOKEN) {
  console.error('Missing Mapbox token! Set REACT_APP_MAPBOX_TOKEN in your .env file');
}

// Helper function to get location name using the Mapbox Geocoding API
const getLocationName = async (latitude: number, longitude: number): Promise<string> => {
  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_TOKEN}&types=place,locality,neighborhood&limit=1`
    );
    const data = await response.json();
    return data.features && data.features.length > 0 
      ? data.features[0].place_name 
      : '';
  } catch (error) {
    console.error('Error fetching location name:', error);
    return '';
  }
};

// Define the search result interface
interface SearchResult {
  place_name: string;
  center: [number, number]; // [longitude, latitude]
  id: string;
}

// Helper function to search for places or parse coordinates
const searchPlaces = async (query: string): Promise<SearchResult[]> => {
  // Check if input looks like coordinates (simple regex check)
  const coordsRegex = /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/;
  
  if (coordsRegex.test(query)) {
    // Parse as coordinates
    const [lng, lat] = query.split(',').map(coord => parseFloat(coord.trim()));
    
    if (!isNaN(lng) && !isNaN(lat)) {
      // Create a fake result object with the parsed coordinates
      return [{
        place_name: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
        center: [lng, lat],
        id: 'custom-coords'
      }];
    }
  }
  
  // Otherwise search using the Mapbox Geocoding API
  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&limit=5`
    );
    const data = await response.json();
    return data.features || [];
  } catch (error) {
    console.error('Error searching places:', error);
    return [];
  }
};

interface MapboxMapProps {
  latitude: number;
  longitude: number;
  zoom: number;
  rowDirection: number;
  rowDistance: number;
  onLocationChange: (lat: number, lng: number) => void;
  children?: React.ReactNode;
  hideControls?: boolean;
  savedLocations?: SavedLocation[];
  selectedSavedLocationId?: string | null;
  onSavedLocationSelect?: (locationId: string) => void;
  routeVisible?: boolean;
  routeGeometry?: {
    betweenLocations: any | null;
    fromCurrentLocation: any | null;
  };
  onUpdateRouteGeometry?: (geometry: any) => void;
}

// Utility functions for coordinate conversion
const decimalToDegrees = (decimal: number, isLatitude: boolean): string => {
  const absolute = Math.abs(decimal);
  const degrees = Math.floor(absolute);
  const minutes = Math.floor((absolute - degrees) * 60);
  const seconds = ((absolute - degrees - minutes / 60) * 3600).toFixed(1);
  
  // Direction suffix based on hemisphere
  const direction = isLatitude
    ? decimal >= 0 ? 'N' : 'S'
    : decimal >= 0 ? 'E' : 'W';
  
  return `${degrees}°${minutes}'${seconds}"${direction}`;
};

const isValidCoordinates = (text: string): { valid: boolean, lat?: number, lng?: number } => {
  // Check for decimal format: lat,lng (e.g. 43.662929, 3.951927)
  const decimalRegex = /^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/;
  const decimalMatch = text.match(decimalRegex);
  
  if (decimalMatch) {
    const lat = parseFloat(decimalMatch[1]);
    const lng = parseFloat(decimalMatch[2]);
    
    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { valid: true, lat, lng };
    }
  }
  
  // Check for DMS format (e.g. 43°38'43.0"N 3°59'25.7"E)
  // This regex matches degrees, minutes, seconds format with direction indicators
  const dmsRegex = /(\d+)°(\d+)'([\d.]+)"([NS])\s+(\d+)°(\d+)'([\d.]+)"([EW])/i;
  const dmsMatch = text.match(dmsRegex);
  
  if (dmsMatch) {
    try {
      // Extract lat components (degrees, minutes, seconds, direction)
      const latDeg = parseInt(dmsMatch[1]);
      const latMin = parseInt(dmsMatch[2]);
      const latSec = parseFloat(dmsMatch[3]);
      const latDir = dmsMatch[4].toUpperCase();
      
      // Extract lng components
      const lngDeg = parseInt(dmsMatch[5]);
      const lngMin = parseInt(dmsMatch[6]);
      const lngSec = parseFloat(dmsMatch[7]);
      const lngDir = dmsMatch[8].toUpperCase();
      
      // Validate components
      if (
        latDeg <= 90 && latMin < 60 && latSec < 60 &&
        lngDeg <= 180 && lngMin < 60 && lngSec < 60
      ) {
        // Convert DMS to decimal
        let lat = latDeg + (latMin / 60) + (latSec / 3600);
        let lng = lngDeg + (lngMin / 60) + (lngSec / 3600);
        
        // Apply direction
        if (latDir === 'S') lat = -lat;
        if (lngDir === 'W') lng = -lng;
        
        return { valid: true, lat, lng };
      }
    } catch (e) {
      console.error('Error parsing DMS format:', e);
    }
  }
  
  return { valid: false };
};

const MapboxMap: React.FC<MapboxMapProps> = ({
  latitude,
  longitude,
  zoom,
  rowDirection,
  rowDistance,
  onLocationChange,
  children,
  hideControls = false,
  savedLocations = [],
  selectedSavedLocationId = null,
  onSavedLocationSelect,
  routeVisible = false,
  routeGeometry,
  onUpdateRouteGeometry
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [viewState, setViewState] = useState({
    latitude,
    longitude,
    zoom,
    bearing: 0,
    pitch: 0
  });
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  // Store user location for bounding box calculation
  const [userLocation, setUserLocation] = useState<{ latitude: number, longitude: number } | null>(null);
  // Track if the initial bounding box has been set
  const [initialBoundingBoxSet, setInitialBoundingBoxSet] = useState(false);
  // Track if map has fully rendered
  const mapRenderedRef = useRef(false);

  // Add states for coordinate editing and notifications
  const [editableCoords, setEditableCoords] = useState<string>('');
  const [isEditingCoords, setIsEditingCoords] = useState<boolean>(false);
  const [notification, setNotification] = useState<{message: string, severity: 'success' | 'error'} | null>(null);

  // Get user's location on component mount and add to locations for initial view
  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude: userLat, longitude: userLng } = position.coords;
          // Store user location for later use in bounding box calculation
          setUserLocation({ latitude: userLat, longitude: userLng });
          onLocationChange(userLat, userLng);
        },
        (error) => {
          console.error("Error getting user location:", error);
          // Fall back to default location if geolocation fails
          setUserLocation({ latitude: latitude, longitude: longitude });
          onLocationChange(latitude, longitude);
        }
      );
    } else {
      // Fall back to default location if geolocation is not supported
      console.log("Geolocation is not supported by this browser");
      setUserLocation({ latitude: latitude, longitude: longitude });
      onLocationChange(latitude, longitude);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // We only want to run this effect once on mount, not when props change
  }, []);

  // Calculate bounding box and set appropriate view state
  const calculateBoundingBox = useCallback(() => {
    // Only run if we have saved locations OR a user location AND the map is ready
    if ((savedLocations.length > 0 || userLocation) && isMapReady) {
      console.log('Calculating bounds for', savedLocations.length, 'locations plus user location');
      
      // Start with current location bounds
      let bounds: { 
        minLat: number; 
        maxLat: number; 
        minLng: number; 
        maxLng: number 
      };
      
      // Initialize bounds
      if (savedLocations.length > 0) {
        // Initialize with the first saved location
        bounds = { 
          minLat: savedLocations[0].latitude, 
          maxLat: savedLocations[0].latitude, 
          minLng: savedLocations[0].longitude, 
          maxLng: savedLocations[0].longitude 
        };
        
        // Include all other saved locations
        bounds = savedLocations.reduce((acc, location) => {
          return {
            minLat: Math.min(acc.minLat, location.latitude),
            maxLat: Math.max(acc.maxLat, location.latitude),
            minLng: Math.min(acc.minLng, location.longitude),
            maxLng: Math.max(acc.maxLng, location.longitude)
          };
        }, bounds);
      } else {
        // No saved locations, start with user location
        bounds = { 
          minLat: userLocation!.latitude, 
          maxLat: userLocation!.latitude, 
          minLng: userLocation!.longitude, 
          maxLng: userLocation!.longitude 
        };
      }
      
      // Include user location in the bounds if available
      if (userLocation) {
        bounds = {
          minLat: Math.min(bounds.minLat, userLocation.latitude),
          maxLat: Math.max(bounds.maxLat, userLocation.latitude),
          minLng: Math.min(bounds.minLng, userLocation.longitude),
          maxLng: Math.max(bounds.maxLng, userLocation.longitude)
        };
      }
      
      // Calculate center point
      const centerLat = (bounds.minLat + bounds.maxLat) / 2;
      const centerLng = (bounds.minLng + bounds.maxLng) / 2;
      
      // Calculate the range
      const latRange = Math.max(0.01, bounds.maxLat - bounds.minLat); // Ensure non-zero range
      const lngRange = Math.max(0.01, bounds.maxLng - bounds.minLng); // Ensure non-zero range
      
      // Add 10% padding to bounds (ensure no edge is closer than 10% to edge of map)
      const latPadding = Math.max(0.01, latRange * 0.1); // Use at least 0.01 degrees minimum padding
      const lngPadding = Math.max(0.01, lngRange * 0.1);
      
      bounds.minLat -= latPadding;
      bounds.maxLat += latPadding;
      bounds.minLng -= lngPadding;
      bounds.maxLng += lngPadding;
      
      // Calculate new bounds with padding
      const paddedLatRange = bounds.maxLat - bounds.minLat;
      const paddedLngRange = bounds.maxLng - bounds.minLng;
      
      // Calculate zoom level to fit the bounds considering map aspect ratio
      // We get the map container dimensions to calculate the right aspect ratio
      let mapWidth = 1;
      let mapHeight = 1;
      
      if (mapContainer.current) {
        const rect = mapContainer.current.getBoundingClientRect();
        mapWidth = Math.max(1, rect.width);
        mapHeight = Math.max(1, rect.height);
        console.log('Map dimensions:', mapWidth, 'x', mapHeight);
      }
      
      // Calculate the zoom level needed for both lat and lng ranges
      const aspectRatio = mapWidth / mapHeight;
      const latZoom = Math.log2(360 / (paddedLatRange * aspectRatio)) - 1;
      const lngZoom = Math.log2(360 / paddedLngRange) - 1;
      
      // Use the smaller zoom level to ensure everything fits
      let newZoom = Math.min(latZoom, lngZoom);
      
      // Limit zoom to reasonable values
      newZoom = Math.min(Math.max(newZoom, 4), 15);
      
      console.log('Calculated center:', centerLat, centerLng, 'zoom:', newZoom);
      console.log('Bounds with padding:', bounds);
      
      // Update the view state
      setViewState(prev => ({
        ...prev,
        latitude: centerLat,
        longitude: centerLng,
        zoom: newZoom
      }));
      
      // Mark that we've set the initial bounding box
      setInitialBoundingBoxSet(true);
    }
  }, [savedLocations, isMapReady, userLocation]);

  // Set initial zoom to show all saved locations and user location
  useEffect(() => {
    calculateBoundingBox();
  }, [calculateBoundingBox]);

  // Update viewState when props change but not when still calculating initial bounds
  useEffect(() => {
    // Skip this update if the initial bounding box hasn't been set yet
    // This ensures the bounding box calculation takes precedence
    if (initialBoundingBoxSet) {
    // Only update latitude and longitude, preserve local zoom state
    // This prevents autozoom when clicking on the map
    setViewState(prev => ({
      ...prev,
      latitude,
      longitude,
      // Only update zoom from props on initial load or explicit zoom changes
      // but not when just the location changes through clicks
      zoom: prev.zoom || zoom
    }));
    } else {
      console.log('Skipping view state update to prioritize bounding box calculation');
    }
  }, [latitude, longitude, zoom, initialBoundingBoxSet]);

  // Trigger a recalculation after the map has fully rendered
  const handleMapLoad = useCallback(() => {
    console.log('Map fully rendered');
    mapRenderedRef.current = true;
    // Wait a bit to ensure dimensions are stable, then recalculate
    setTimeout(() => {
      console.log('Recalculating bounds after map render');
      calculateBoundingBox();
    }, 500);
  }, [calculateBoundingBox]);

  // Get location name when coordinates change
  useEffect(() => {
    const fetchLocationName = async () => {
      const name = await getLocationName(latitude, longitude);
      setLocationName(name);
    };
    
    if (MAPBOX_TOKEN) {
      fetchLocationName();
    }
  }, [latitude, longitude]);

  // Set map ready state after component mounts
  useEffect(() => {
    if (mapContainer.current) {
      setIsMapReady(true);
    }
  }, []);

  // Hide help message after first click
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowHelp(false);
    }, 8000);
    
    return () => clearTimeout(timer);
  }, []);

  // Clean up search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  // Add keyboard shortcut (Escape) to close search
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isSearchExpanded) {
        setIsSearchExpanded(false);
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isSearchExpanded]);

  // Update the editable coordinates when location changes
  useEffect(() => {
    setEditableCoords(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
  }, [latitude, longitude]);

  const handleViewStateChange = useCallback((evt: ViewStateChangeEvent) => {
    setViewState(evt.viewState);
  }, []);

  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const results = await searchPlaces(query);
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleResultSelect = useCallback((result: SearchResult | null) => {
    if (result) {
      // Extract coordinates and update location
      const [lng, lat] = result.center;
      onLocationChange(lat, lng);
      setIsSearchOpen(false);
      setSearchQuery('');
      setIsSearchExpanded(false);
    }
  }, [onLocationChange]);

  const handleMapClick = useCallback((evt: any) => {
    const { lngLat } = evt;
    // Update only the location without changing the zoom level
    onLocationChange(lngLat.lat, lngLat.lng);
    
    // Store current zoom level in view state to prevent auto-zooming
    setViewState(prev => ({
      ...prev,
      latitude: lngLat.lat,
      longitude: lngLat.lng
      // Intentionally not updating zoom to preserve current level
    }));
    
    setShowHelp(false);
    // Close the search field when clicking on the map
    if (isSearchExpanded) {
      setIsSearchExpanded(false);
      setIsSearchOpen(false);
    }
  }, [onLocationChange, isSearchExpanded]);

  // Handle copy coordinates
  const handleCopyCoords = () => {
    // Format for Google Maps search
    const googleMapsFormat = `${latitude.toFixed(6)},${longitude.toFixed(6)}`;
    
    navigator.clipboard.writeText(googleMapsFormat)
      .then(() => {
        setNotification({
          message: 'Coordinates copied to clipboard',
          severity: 'success'
        });
      })
      .catch(err => {
        console.error('Failed to copy coordinates:', err);
        setNotification({
          message: 'Failed to copy coordinates',
          severity: 'error'
        });
      });
  };
  
  // Handle paste coordinates
  const handlePasteCoords = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      const result = isValidCoordinates(clipboardText);
      
      if (result.valid && result.lat !== undefined && result.lng !== undefined) {
        onLocationChange(result.lat, result.lng);
        setNotification({
          message: 'Coordinates pasted and applied',
          severity: 'success'
        });
      } else {
        setNotification({
          message: 'Invalid coordinate format. Use decimal (43.662929, 3.951927) or DMS (43°38\'43.0"N 3°59\'25.7"E)',
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('Failed to paste coordinates:', err);
      setNotification({
        message: 'Failed to paste coordinates',
        severity: 'error'
      });
    }
  };
  
  // Handle coordinate manual edit
  const handleCoordsEdit = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditableCoords(e.target.value);
  };
  
  // Handle coordinate edit submission
  const handleCoordsSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const result = isValidCoordinates(editableCoords);
      
      if (result.valid && result.lat !== undefined && result.lng !== undefined) {
        onLocationChange(result.lat, result.lng);
        setIsEditingCoords(false);
        setNotification({
          message: 'Coordinates updated',
          severity: 'success'
        });
      } else {
        setNotification({
          message: 'Invalid coordinate format. Use decimal (43.662929, 3.951927) or DMS (43°38\'43.0"N 3°59\'25.7"E)',
          severity: 'error'
        });
      }
    } else if (e.key === 'Escape') {
      // Reset to current coordinates
      setEditableCoords(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
      setIsEditingCoords(false);
    }
  };
  
  // Handle notification close
  const handleNotificationClose = () => {
    setNotification(null);
  };

  // Custom marker component for the main selected location
  const CustomMarker = () => (
    <Box sx={{ 
      position: 'relative', 
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1
    }}>
      <Box
        sx={{
          width: 24, 
          height: 24, 
          borderRadius: '50%', 
          backgroundColor: '#e53e3e',
          color: 'white',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: 'bold',
          border: '2px solid white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        }}
      >
        {/* Display order number if available */}
        {selectedSavedLocationId !== null && 
          savedLocations.find(loc => loc.id === selectedSavedLocationId)?.order !== undefined && 
          Number(savedLocations.find(loc => loc.id === selectedSavedLocationId)?.order) + 1}
      </Box>
    </Box>
  );

  // Red marker for saved locations
  const SavedLocationMarker = ({ location }: { location: SavedLocation }) => (
    <Box sx={{ 
      position: 'relative', 
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1
    }}>
      <Box
        sx={{
          width: 24, 
          height: 24, 
          borderRadius: '50%', 
          backgroundColor: '#e53e3e',
          color: 'white',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: 'bold',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        }}
      >
        {location.order !== undefined ? Number(location.order) + 1 : ''}
      </Box>
    </Box>
  );

  // Create GeoJSON for route line between locations
  const routeLineGeoJSON = useMemo((): GeoJSON.Feature<GeoJSON.LineString> => {
    if (!routeVisible || !savedLocations || savedLocations.length < 2) {
      return {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: []
        }
      };
    }

    // If we have a route geometry from the Mapbox Directions API, use it
    if (routeGeometry && routeGeometry.betweenLocations) {
      return {
        type: 'Feature',
        properties: {},
        geometry: routeGeometry.betweenLocations as GeoJSON.LineString
      };
    }

    // Fallback: Sort locations by order property and create a simple line
    const sortedLocations = [...savedLocations].sort((a, b) => {
      const orderA = a.order !== undefined ? a.order : 999;
      const orderB = b.order !== undefined ? b.order : 999;
      return orderA - orderB;
    });

    // Create a LineString from the locations
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: sortedLocations.map(loc => [loc.longitude, loc.latitude])
      }
    };
  }, [savedLocations, routeVisible, routeGeometry]);
  
  // Create GeoJSON for route line from current location to first location
  const currentLocationRouteGeoJSON = useMemo((): GeoJSON.Feature<GeoJSON.LineString> => {
    if (!routeVisible || !routeGeometry || !routeGeometry.fromCurrentLocation) {
      return {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: []
        }
      };
    }

    return {
      type: 'Feature',
      properties: {},
      geometry: routeGeometry.fromCurrentLocation as GeoJSON.LineString
    };
  }, [routeVisible, routeGeometry]);

  return (
    <Box 
      sx={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}
      ref={mapContainer}
    >
      {isMapReady && MAPBOX_TOKEN && (
        <>
          <Map
            {...viewState}
            onMove={handleViewStateChange}
            onClick={handleMapClick}
            mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
            mapboxAccessToken={MAPBOX_TOKEN}
            style={{ width: '100%', height: '100%' }}
            cursor="grab"
            onLoad={handleMapLoad}
          >
            {!hideControls && <NavigationControl position="top-right" showCompass showZoom />}
            
            {/* Custom marker at the selected location */}
            <Marker
              longitude={longitude}
              latitude={latitude}
              anchor="center"
            >
              <CustomMarker />
            </Marker>
            
            {/* Add markers for saved locations (except for the currently selected one) */}
            {savedLocations.map(location => (
              location.id !== selectedSavedLocationId && (
                <Marker
                  key={location.id}
                  longitude={location.longitude}
                  latitude={location.latitude}
                  anchor="center"
                  onClick={(e) => {
                    // Prevent the click from propagating to the map
                    e.originalEvent.stopPropagation();
                    // If onSavedLocationSelect is provided, call it with the location ID
                    if (onSavedLocationSelect) {
                      onSavedLocationSelect(location.id);
                    } else {
                      // Otherwise just navigate to the location
                      onLocationChange(location.latitude, location.longitude);
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      cursor: 'pointer'
                    }}
                  >
                    <SavedLocationMarker location={location} />
                  </Box>
                </Marker>
              )
            ))}
            
            {/* Add current user location marker */}
            {userLocation && (
              <Marker
                longitude={userLocation.longitude}
                latitude={userLocation.latitude}
                anchor="center"
              >
                <Box sx={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  bgcolor: '#1976d2',
                  border: '2px solid white',
                  boxShadow: '0 0 4px rgba(0,0,0,0.4)',
                }} />
              </Marker>
            )}
            
            {/* Vineyard rows overlay */}
            <VineyardRowsOverlay
              latitude={latitude}
              longitude={longitude}
              rowDirection={rowDirection}
              rowDistance={rowDistance}
              zoom={viewState.zoom}
            />

            {/* Route line layers */}
            {routeVisible && savedLocations && savedLocations.length >= 2 && (
              <>
                {/* Route between locations */}
                <Source
                  id="route-line" 
                  type="geojson" 
                  data={routeLineGeoJSON}
                >
                  <Layer
                    id="route-layer"
                    type="line"
                    paint={{
                      'line-color': 'rgba(255, 0, 0, 0.6)',
                      'line-width': 4,
                      'line-opacity': 0.7
                    }}
                  />
                </Source>
                
                {/* Route from current location to first location */}
                {routeGeometry && routeGeometry.fromCurrentLocation && (
                  <Source
                    id="current-route-line" 
                    type="geojson" 
                    data={currentLocationRouteGeoJSON}
                  >
                    <Layer
                      id="current-route-layer"
                      type="line"
                      paint={{
                        'line-color': 'rgba(25, 118, 210, 0.7)',
                        'line-width': 4,
                        'line-opacity': 0.7
                      }}
                    />
                  </Source>
                )}
              </>
            )}

            {/* Additional children (like markers) */}
            {children}
          </Map>
          
          {/* Help tooltip */}
          {!hideControls && showHelp && (
            <Tooltip
              open={true}
              title="Click anywhere on the map to place your vineyard"
              arrow
              placement="top"
              sx={{ 
                position: 'absolute', 
                bottom: '50%', 
                left: '50%', 
                transform: 'translateX(-50%)',
                zIndex: 10
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  padding: '8px 16px',
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(5px)',
                  display: 'flex',
                  alignItems: 'center',
                  zIndex: 10,
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  maxWidth: '80%'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3182CE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', flexShrink: 0 }}>
                  <polygon points="3 11 22 2 13 21 11 13 3 11"></polygon>
                </svg>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Click anywhere on the map to place your vineyard
                </Typography>
              </Paper>
            </Tooltip>
          )}
          
          {/* Notification snackbar */}
          <Snackbar
            open={notification !== null}
            autoHideDuration={2000}
            onClose={handleNotificationClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            message={notification?.message || ""}
          >
            <Alert 
              onClose={handleNotificationClose} 
              severity={notification?.severity || "success"}
              sx={{ 
                width: '100%', 
                maxWidth: '500px' 
              }}
              variant="filled"
            >
              {notification?.message || ""}
            </Alert>
          </Snackbar>
          
          {/* Location coordinates display */}
          {!hideControls && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                padding: '8px 12px',
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(5px)',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 10,
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#4A5568' }}>
                LOCATION
              </Typography>
                <Box>
                  <IconButton 
                    size="small" 
                    onClick={handleCopyCoords}
                    sx={{ padding: '2px', mr: 0.5 }}
                    title="Copy coordinates"
                  >
                    <FileCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={handlePasteCoords}
                    sx={{ padding: '2px' }}
                    title="Paste coordinates"
                  >
                    <ContentPasteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
              
              {isEditingCoords ? (
                <TextField
                  size="small"
                  value={editableCoords}
                  onChange={handleCoordsEdit}
                  onKeyDown={handleCoordsSubmit}
                  onBlur={() => setIsEditingCoords(false)}
                  autoFocus
                  placeholder="lat, lng"
                  variant="standard"
                  fullWidth
                  InputProps={{
                    sx: { 
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#2D3748',
                      p: 0
                    }
                  }}
                />
              ) : (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500, 
                    color: '#2D3748',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                  onClick={() => setIsEditingCoords(true)}
                >
                {latitude.toFixed(6)}, {longitude.toFixed(6)}
              </Typography>
              )}
            </Paper>
          )}
          
          {/* Location name display */}
          {!hideControls && locationName && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                padding: '8px 12px',
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(5px)',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 10,
                maxWidth: '60%',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500, color: '#2D3748' }}>
                {locationName}
              </Typography>
            </Paper>
          )}
          
          {/* Search box */}
          {!hideControls && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                top: locationName ? '70px' : 16,
                left: 16,
                borderRadius: isSearchExpanded ? 2 : 24,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(5px)',
                zIndex: 20,
                width: isSearchExpanded ? '300px' : '48px',
                height: isSearchExpanded ? 'auto' : '48px',
                display: 'flex',
                alignItems: 'center', 
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                overflow: 'hidden'
              }}
            >
              {!isSearchExpanded ? (
                <IconButton 
                  sx={{ p: 1.5, width: '100%', height: '100%' }}
                  onClick={() => setIsSearchExpanded(true)}
                  aria-label="Search"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3182CE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </IconButton>
              ) : (
                <Autocomplete
                  open={isSearchOpen}
                  onOpen={() => setIsSearchOpen(true)}
                  onClose={() => setIsSearchOpen(false)}
                  inputValue={searchQuery}
                  onInputChange={(_, newValue) => {
                    setSearchQuery(newValue);
                    if (searchTimeout.current) {
                      clearTimeout(searchTimeout.current);
                    }
                    searchTimeout.current = setTimeout(() => {
                      handleSearch(newValue);
                    }, 500);
                  }}
                  onChange={(_, newValue) => handleResultSelect(newValue)}
                  options={searchResults}
                  getOptionLabel={(option) => option.place_name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  loading={isSearching}
                  filterOptions={(x) => x} // Disable client-side filtering
                  noOptionsText="Enter a place name or lat,lng coordinates"
                  sx={{ width: '100%' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      fullWidth
                      placeholder="Search for a place or lat,lng"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'transparent',
                          },
                          '&:hover fieldset': {
                            borderColor: 'transparent',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'transparent',
                          },
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3182CE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="11" cy="11" r="8"></circle>
                              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <>
                            {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                            <InputAdornment position="end">
                              <IconButton 
                                size="small"
                                onClick={() => {
                                  setSearchQuery('');
                                  setIsSearchExpanded(false);
                                }}
                                edge="end"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#718096" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <line x1="18" y1="6" x2="6" y2="18"></line>
                                  <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                              </IconButton>
                            </InputAdornment>
                          </>
                        )
                      }}
                      autoFocus
                    />
                  )}
                />
              )}
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default MapboxMap; 